import { Client } from '@microsoft/microsoft-graph-client';
import { AuthenticationProvider } from '@microsoft/microsoft-graph-client';
import { PublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { logger } from '../utils/logger';

// Provider de autenticação para Microsoft Graph
class GraphAuthProvider implements AuthenticationProvider {
  private msalInstance: PublicClientApplication;
  private account: AccountInfo | null = null;

  constructor(msalInstance: PublicClientApplication) {
    this.msalInstance = msalInstance;
  }

  setAccount(account: AccountInfo | null) {
    this.account = account;
  }

  async getAccessToken(): Promise<string> {
    let account = this.account;

    logger.debug('GraphAuthProvider: Tentando obter token...');
    logger.debug('Account definida:', !!account);

    // Se não há conta definida, tentar obter a conta ativa
    if (!account) {
      logger.debug('Buscando contas ativas...');
      const accounts = this.msalInstance.getAllAccounts();
      logger.debug('Contas encontradas:', accounts.length);
      
      if (accounts.length > 0) {
        account = accounts[0];
        this.account = account;
        logger.debug('Conta definida:', account.username);
      } else {
        logger.error('Nenhuma conta encontrada');
        throw new Error('No account found');
      }
    }

    try {
      logger.debug('Solicitando token silencioso...');
      const response = await this.msalInstance.acquireTokenSilent({
        scopes: ['User.Read', 'User.ReadBasic.All', 'Group.Read.All'],
        account: account,
      });

      logger.debug('Token obtido com sucesso');
      return response.accessToken;
    } catch (error) {
      logger.error('Erro ao obter token:', error);
      throw error;
    }
  }
}

// Serviço para interagir com Microsoft Graph
export class MicrosoftGraphService {
  private graphClient: Client;
  private authProvider: GraphAuthProvider;

  constructor(msalInstance: PublicClientApplication) {
    logger.info('Inicializando MicrosoftGraphService...');
    this.authProvider = new GraphAuthProvider(msalInstance);
    this.graphClient = Client.initWithMiddleware({
      authProvider: this.authProvider,
    });
    
    // Tentar obter conta ativa automaticamente
    this.initializeAccount(msalInstance);
    logger.success('MicrosoftGraphService inicializado');
  }

  private async initializeAccount(msalInstance: PublicClientApplication) {
    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        this.setAccount(accounts[0]);
      }
    } catch (error) {
      logger.debug('No active account found during initialization');
    }
  }

  setAccount(account: AccountInfo | null) {
    this.authProvider.setAccount(account);
  }

  // Buscar usuários do domínio
  async searchUsers(query: string, domains: string[] = ['@cidadania4u.com.br', '@becivis.com']): Promise<any[]> {
    try {
      const searchQuery = domains.map(domain => `mail:${query}*${domain}`).join(' OR ');
      
      const response = await this.graphClient
        .api('/users')
        .search(query)
        .select('id,displayName,mail,userPrincipalName,jobTitle,department')
        .filter(`startswith(mail,'${query}') and (${domains.map(d => `endswith(mail,'${d}')`).join(' or ')})`)
        .top(50)
        .get();

      return response.value || [];
    } catch (error) {
      logger.error('Error searching users:', error);
      throw error;
    }
  }

  // Buscar grupos do usuário
  async getUserGroups(userId: string): Promise<any[]> {
    try {
      const response = await this.graphClient
        .api(`/users/${userId}/memberOf`)
        .select('id,displayName,groupTypes')
        .get();

      return response.value || [];
    } catch (error) {
      logger.error('Error getting user groups:', error);
      throw error;
    }
  }

  // Obter informações do usuário atual
  async getCurrentUser(): Promise<any> {
    try {
      const response = await this.graphClient
        .api('/me')
        .select('id,displayName,mail,userPrincipalName,jobTitle,department')
        .get();

      return response;
    } catch (error) {
      logger.error('Error getting current user:', error);
      throw error;
    }
  }

  // Verificar se usuário pertence a um grupo específico
  async isUserInGroup(userId: string, groupId: string): Promise<boolean> {
    try {
      const groups = await this.getUserGroups(userId);
      return groups.some(group => group.id === groupId);
    } catch (error) {
      logger.error('Error checking user group membership:', error);
      return false;
    }
  }

  // Obter todos os usuários de um grupo
  async getGroupMembers(groupId: string): Promise<any[]> {
    try {
      const response = await this.graphClient
        .api(`/groups/${groupId}/members`)
        .select('id,displayName,mail,userPrincipalName,jobTitle,department')
        .get();

      return response.value || [];
    } catch (error) {
      logger.error('Error getting group members:', error);
      throw error;
    }
  }

  // Verificar domínio do email
  isAuthorizedDomain(email: string, authorizedDomains: string[]): boolean {
    return authorizedDomains.some(domain => email.endsWith(domain));
  }

  // Mapear grupos do Azure para roles do sistema
  mapAzureGroupToRole(groups: any[], azureGroups: any): string {
    logger.debug('Mapeando grupos do Azure:', groups.map(g => ({ id: g.id, displayName: g.displayName })));
    logger.debug('Grupos configurados:', azureGroups);
    
    // Verificar por ID do grupo
    if (groups.some(group => group.id === azureGroups.MASTER)) {
      logger.info('Usuário mapeado para role: master (por ID)');
      return 'master';
    }
    if (groups.some(group => group.id === azureGroups.ADMIN)) {
      logger.info('Usuário mapeado para role: admin (por ID)');
      return 'admin';
    }
    if (groups.some(group => group.id === azureGroups.USER)) {
      logger.info('Usuário mapeado para role: user (por ID)');
      return 'user';
    }
    
    // Verificar por nome do grupo (fallback)
    if (groups.some(group => group.displayName?.toLowerCase().includes('master'))) {
      logger.info('Usuário mapeado para role: master (por nome)');
      return 'master';
    }
    if (groups.some(group => group.displayName?.toLowerCase().includes('admin'))) {
      logger.info('Usuário mapeado para role: admin (por nome)');
      return 'admin';
    }
    if (groups.some(group => group.displayName?.toLowerCase().includes('user'))) {
      logger.info('Usuário mapeado para role: user (por nome)');
      return 'user';
    }
    
    logger.warn('Nenhum grupo correspondente encontrado, usando role padrão: user');
    return 'user'; // Default role
  }
}
